import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Button,
} from "@mui/material";
import {
  Person,
  Email,
  Badge,
  PersonAdd,
  CheckCircle,
  Warning,
  Phone,
  OpenInNew,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useOwner } from "../../../../context/OwnerContext";
import { useRole } from "../../../../context/RoleContext";

const RelationshipManager = () => {
  const { owner, loading, error } = useOwner();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box className="p-4">
        <Typography>{t("rm.loadingInfo")}</Typography>
      </Box>
    );
  }

  if (error || !owner) {
    return (
      <Box className="p-4">
        <Typography color="error">{error || "Owner not found"}</Typography>
      </Box>
    );
  }

  const getRegistrationStatusColor = (status?: string) => {
    switch (status) {
      case "admin_created": return "success";
      case "pending_approval": return "warning";
      case "approved": return "info";
      case "rejected": return "error";
      default: return "default";
    }
  };

  const getRegistrationStatusIcon = (status?: string) => {
    switch (status) {
      case "admin_created": return <PersonAdd />;
      case "pending_approval": return <Warning />;
      case "approved": return <CheckCircle />;
      default: return <PersonAdd />;
    }
  };

  const getRegistrationStatusLabel = (status?: string) => {
    switch (status) {
      case "admin_created": return t("rm.createdByRM");
      case "pending_approval": return t("rm.pendingApproval");
      case "approved": return t("rm.approved");
      case "rejected": return t("rm.rejected");
      default: return "Unknown";
    }
  };

  const getRoleLabel = (role?: {
    _id: string;
    name: string;
    label?: string;
    permissions?: string[];
  }) => {
    if (role?.name === "super_admin") return t("rm.superAdmin");
    return role?.label ?? role?.name ?? t("rm.roleRM");
  };

  return (
    <Box className="p-4">
      <Typography variant="h5" gutterBottom>
        {t("rm.title")}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Box className="flex items-center gap-3 mb-4">
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <Badge />
                </Avatar>
                <Typography variant="h6">{t("rm.registrationStatus")}</Typography>
              </Box>

              <Box className="flex items-center gap-2 mb-3">
                <Chip
                  icon={getRegistrationStatusIcon(owner.registrationStatus)}
                  label={getRegistrationStatusLabel(owner.registrationStatus)}
                  color={getRegistrationStatusColor(owner.registrationStatus) as any}
                  size="medium"
                />
              </Box>

              {owner.registrationStatus === "admin_created" && (
                <Typography variant="body2" color="text.secondary">
                  {t("rm.createdByRMDesc")}
                </Typography>
              )}
              {owner.registrationStatus === "pending_approval" && (
                <Typography variant="body2" color="text.secondary">
                  {t("rm.selfRegisteredDesc")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {owner.relationshipManager && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Box className="flex items-center gap-3 mb-4">
                  <Avatar sx={{ bgcolor: "success.main" }}>
                    <Person />
                  </Avatar>
                  <Typography variant="h6">
                    {t("rm.assignedRM")}
                  </Typography>
                </Box>

                <List>
                  {(owner.relationshipManager.firstName ||
                    owner.relationshipManager.lastName) && (
                    <ListItem>
                      <ListItemIcon><Person /></ListItemIcon>
                      <ListItemText
                        primary={t("rm.fullName")}
                        secondary={
                          `${owner.relationshipManager.firstName || ""} ${owner.relationshipManager.lastName || ""}`.trim() ||
                          "—"
                        }
                      />
                    </ListItem>
                  )}

                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText
                      primary={t("rm.username")}
                      secondary={owner.relationshipManager.username}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon><Badge /></ListItemIcon>
                    <ListItemText
                      primary={t("rm.role")}
                      secondary={getRoleLabel(owner.relationshipManager.role)}
                    />
                  </ListItem>

                  {owner.relationshipManager.email && (
                    <ListItem>
                      <ListItemIcon><Email /></ListItemIcon>
                      <ListItemText
                        primary={t("rm.email")}
                        secondary={owner.relationshipManager.email}
                      />
                    </ListItem>
                  )}

                  {owner.relationshipManager.phone && (
                    <ListItem>
                      <ListItemIcon><Phone /></ListItemIcon>
                      <ListItemText
                        primary={t("rm.phone")}
                        secondary={`${owner.relationshipManager.country_code || ""} ${owner.relationshipManager.phone}`.trim()}
                      />
                    </ListItem>
                  )}
                </List>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: "block" }}
                >
                  {t("rm.rmManagesAccount")}
                </Typography>

                {isSuperAdmin && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNew />}
                      onClick={() =>
                        navigate(
                          `/dashboard/admins/${owner.relationshipManager!._id}`
                        )
                      }
                    >
                      {t("rm.viewRMDashboard")}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {!owner.relationshipManager && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Box className="flex items-center gap-3 mb-4">
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <Warning />
                  </Avatar>
                  <Typography variant="h6">
                    {t("rm.noRM")}
                  </Typography>
                </Box>

                <Typography variant="body1" color="text.secondary">
                  {t("rm.noRMDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default RelationshipManager;
